import { useCallback, useMemo } from 'react';
import { useTranslation, TFunction } from 'react-i18next';

import styled from '@emotion/styled';

import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import Cols from '__components/Layout/Cols';
import Switch from '__components/Kizen/Switch';
import InputControl from '__components/Inputs/InputControl';

import { GenericOption } from 'pages/SmartConnectors/types';

type ActionOption =
  | 'stop_and_fail'
  | 'stop_and_complete'
  | 'stop_and_cancel'
  | 'pause_and_error'
  | 'pause';

export type Values = {
  type: string;
  action: ActionOption;
  notify: boolean;
};

const StyledInputControl = styled(InputControl)`
  & > div {
    margin: 10px 0 0 0;
  }
`;

export const initState: { action: ActionOption | undefined; notify: boolean } =
  {
    action: undefined,
    notify: true,
  };

export const translatedOptions = (t: TFunction) => [
  { value: 'stop_and_fail', label: t('Stop and mark as Failed') },
  {
    value: 'stop_and_complete',
    label: t('Stop and mark as Successfully Completed'),
  },
  { value: 'stop_and_cancel', label: t('Stop and mark as Cancelled') },
  { value: 'pause_and_error', label: t('Pause and Error') },
  { value: 'pause', label: t('Pause') },
];

const suppressNotifyOptions: ActionOption[] = [
  'stop_and_complete',
  'stop_and_cancel',
  'pause',
];

export type Props = {
  values: Values;
  onChange: (...args: any[]) => void;
  step: any;
};

export const Component = function StopExecution(props: Props) {
  const { t } = useTranslation();
  const { values, onChange } = props;
  const { action, notify } = values;

  const actionOptions = useMemo(() => translatedOptions(t), [t]);

  const handleOnChangeAction = useCallback(
    ({ value: action }: GenericOption<ActionOption>) => {
      let { notify } = values;
      if (suppressNotifyOptions.includes(action)) {
        notify = false;
      }
      onChange({ ...values, action, notify });
    },
    [onChange, values]
  );

  const handleOnChangeNotify = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...values, notify: event.target.checked }),
    [onChange, values]
  );

  const disableNotify = useMemo(
    () => suppressNotifyOptions.includes(action),
    [action]
  );

  return (
    <>
      <Cols columns={3} gutter="20px">
        <Select
          label={t('Action Options')}
          placeholder={t('Choose Action')}
          value={action}
          options={actionOptions}
          onChange={handleOnChangeAction}
          menuInline
          margin
          dataQa="stop-execution-action-options"
        />
        <StyledInputControl label={t('Send Notification')}>
          <Switch
            checked={notify}
            onChange={handleOnChangeNotify}
            disabled={disableNotify}
            dataQa="stop-execution-notify"
          />
        </StyledInputControl>
      </Cols>
    </>
  );
};

export const validate = ({ action }: Values) => action;
